import GalileoAegis from '@tencent/aegis-web-sdk-v2';
import { env as currentEnv } from '@/env/current';
import { DeployEnv } from '@/env/type';
import { getRequestBaseURL } from '@/request/standalone-base-url';
import {
  buildTraceApiConfig,
  filterGalileoLogs,
  getAegisEnv,
  handleApiResponse,
  isGalileoTraceEnabled,
  MONITORING_ENDPOINT_PATTERNS,
} from './aegis-policy';
import { createMonitoringReporter, warnMonitoringFailure } from './aegis-reporter';
import { installDiagnosticSender } from './monitoring/diagnostics';

function createGalileoClient(): GalileoAegis | null {
  try {
    const releaseVersion = Array.from(document.getElementsByTagName('meta'))
      .find(element => element.name === 'x-release-id')
      ?.getAttribute('content') || undefined;

    // 监控需早于登录态加载，用户身份在登录完成后通过 setMonitoringUser 补充。
    const traceConfig = buildTraceApiConfig(
      isGalileoTraceEnabled(import.meta.env.VITE_GALILEO_TRACE_ENABLED),
      getRequestBaseURL(),
    );

    // 本地开发不创建 SDK，避免安装采集插件或将调试数据混入部署环境。
    const aegisEnv = import.meta.env.DEV ? null : getAegisEnv(currentEnv.deployEnv);
    return aegisEnv === null ? null : new GalileoAegis({
      id: 'SDK-540800bc78eba6997056',
      hostUrl: { url: 'https://tgalileo.com/collect' },
      env: aegisEnv,
      version: releaseVersion,
      extField: {
        bizPlatform: currentEnv.bizPlatform,
      },
      repeat: 100,
      plugin: {
        pv: true,
        aid: true,
        error: true,
        device: true,
        close: true,
        pagePerformance: true,
        webVitals: true,
        fId: true,
        spa: true,
        api: {
          apiDetail: true,
          retCodeHandler: handleApiResponse,
          ignoreUrls: [...MONITORING_ENDPOINT_PATTERNS],
          ...traceConfig,
        },
        apiSpeed: true,
        assetSpeed: true,
        session: true,
        blankScreen: currentEnv.deployEnv === DeployEnv.TEST || currentEnv.deployEnv === DeployEnv.PRE,
      },
      onBeforeSend: filterGalileoLogs,
    });
  } catch (error) {
    warnMonitoringFailure('initialization', error);
    return null;
  }
}

const galileoAegis = createGalileoClient();

export const aegis = createMonitoringReporter(galileoAegis);

installDiagnosticSender((diagnostic) => {
  if (diagnostic.kind === 'exception') {
    aegis.error(diagnostic.error);
  } else {
    aegis.reportEvent({ name: 'frontend_diagnostic', ext1: diagnostic.code, ext2: diagnostic.source });
  }
});

export function setMonitoringUser(userName: string): void {
  aegis.setUser(userName);
}
